import { useTheme } from "@emotion/react";
import { Button, Stack, TextField, Typography, Box, Input, InputLabel, Dialog, DialogContent } from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { collectiveApiRequest } from "../../services";
import { totalCreditsAndRemaining } from "../../services/creditsService";
import { remainingCredits } from "../../store/reducers/auth";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";

export function PdfUploadForm({ apiUrl, setJsonData, setStatus, setloader, setCurlMethod, setCurlUrl, setCurlBody }) {
  const theme = useTheme();
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [fileError, setFileError] = useState("");

  const dispatch = useDispatch();

  // Validation Schema
  const schema = yup.object({
    documentName: yup.string().required("Document name is required"),
    signerName: yup.string().required("Signer name is required"),
    signerEmail: yup.string().email("Invalid email").required("Signer email is required"),
    x: yup.number().typeError("Must be number").required("X coordinate required"),
    y: yup.number().typeError("Must be number").required("Y coordinate required"),
    pageNumber: yup.number().typeError("Must be number").required("Page number required"),
  });

  const { register, handleSubmit, reset, getValues, formState: { errors, isValid } } = useForm({
    resolver: yupResolver(schema),
    mode: "onChange",
  });

  // Reset form when API changes
  useEffect(() => {
    reset();
    setFile(null);
    setPreviewUrl(null);
    setFileError("");
  }, [apiUrl]);

  // Handle file upload
  const handleFileUpload = (e) => {
    const uploadedFile = e.target.files[0];
    if (!uploadedFile) return;

    if (uploadedFile.type !== "application/pdf") {
      setFileError("Only PDFs are allowed");
      setFile(null);
      return;
    }

    setFileError("");
    setFile(uploadedFile);
    const url = URL.createObjectURL(uploadedFile);
    setPreviewUrl(url);
    setOpenDialog(true);
  };
  
  useEffect(() => {
    if (isValid && !fileError && file) {
      setCurlMethod("POST");
      setCurlUrl(apiUrl);
  
      const values = getValues();
  
      const signCoordinates = [
        {
          page_num: Number(values.pageNumber),
          x_coord: Number(values.x),
          y_coord: Number(values.y),
        },
      ];
  
      const formData = new FormData();
      formData.append("file", file);
      formData.append("documentName", values.documentName);
      formData.append("signerName", values.signerName);
      formData.append("signerEmail", values.signerEmail);
      formData.append("signCoordinates", JSON.stringify(signCoordinates));
  
      const bodyObj = {};
      for (let [key, value] of formData.entries()) {
        if (key === "file") {
          bodyObj[key] = file.name;
        } else {
          bodyObj[key] = value;
        }
      }
  
      setCurlBody(formData);
    }
  }, [isValid, fileError, file, apiUrl]);
  

  // Submit API request
  const onSubmit = async (data) => {
    try {
      setloader(true);
      setJsonData(null);
      setStatus(null);

      const signCoordinates = [
        {
          page_num: data.pageNumber,
          x_coord: data.x,
          y_coord: data.y,
        }
      ];

      const formData = new FormData();
      formData.append("file", file);
      formData.append("documentName", data.documentName);
      formData.append("signerName", data.signerName);
      formData.append("signerEmail", data.signerEmail);
      formData.append("signCoordinates", JSON.stringify(signCoordinates));

      const res = await collectiveApiRequest(formData, apiUrl);

      setStatus(res?.status);
      setJsonData(res?.data);
      setloader(false);

      // Update remaining credits
      setTimeout(async () => {
        const remainingCredit = await totalCreditsAndRemaining();
        if (remainingCredit?.status === 200) {
          dispatch(
            remainingCredits({
              credits: remainingCredit?.data?.credit?.remainingCredit,
            })
          );
        }
      }, 2000);
      
    } catch (err) {
      setloader(false);
      setStatus(err?.response?.status || 500);
      setJsonData(err?.response?.data || { error: err.message });
    }
  };

  return (
    <>
      <Stack component="form" onSubmit={handleSubmit(onSubmit)} spacing={3} sx={{ mt: 2 }}>

        {/* Upload Box */}
        <Typography variant="body1" sx={{ color: theme.palette.grey.darken4 }}>
          Upload Document
        </Typography>

        <Box
          sx={{
            borderRadius: "12px",
            border: "2px dashed rgba(101, 117, 139, 0.25)",
            minHeight: "120px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
          }}
        >
          <InputLabel htmlFor="upload-pdf" sx={{ textAlign: "center", cursor: "pointer", width: "100%" }}>
            <FileUploadOutlinedIcon sx={{ width: "5rem", height: "5rem" }} />
            <Typography>{file ? file.name : "Click to upload or drag & drop"}</Typography>
          </InputLabel>

          <Input
            id="upload-pdf"
            type="file"
            accept="application/pdf"
            onChange={handleFileUpload}
            style={{ display: "none" }}
          />
        </Box>

        {fileError && <Typography color="error" sx={{ fontSize: "12px" }}>{fileError}</Typography>}

        {/* Input Fields */}
        <TextField label="Document Name" {...register("documentName")} error={!!errors.documentName} helperText={errors.documentName?.message} fullWidth FormHelperTextProps={{ sx: { fontSize: "12px" } }} />
        <TextField label="Signer Name" {...register("signerName")} error={!!errors.signerName} helperText={errors.signerName?.message} fullWidth FormHelperTextProps={{ sx: { fontSize: "12px" } }} />
        <TextField label="Signer Email" {...register("signerEmail")} error={!!errors.signerEmail} helperText={errors.signerEmail?.message} fullWidth FormHelperTextProps={{ sx: { fontSize: "12px" } }} />
        <TextField label="Sign Coordinate X" {...register("x")} error={!!errors.x} helperText={errors.x?.message} fullWidth FormHelperTextProps={{ sx: { fontSize: "12px" } }} />
        <TextField label="Sign Coordinate Y" {...register("y")} error={!!errors.y} helperText={errors.y?.message} fullWidth FormHelperTextProps={{ sx: { fontSize: "12px" } }} />
        <TextField label="Page Number" {...register("pageNumber")} error={!!errors.pageNumber} helperText={errors.pageNumber?.message} fullWidth FormHelperTextProps={{ sx: { fontSize: "12px" } }} />
      
        <Typography sx={{ fontSize: "15px", color: theme.palette.text.secondary, mt: -2, ml: 1 }}>
          0 = <b>All pages</b>, 1 = <b>1st page</b>, -1 = <b>Last page</b> of the document.
        </Typography>

        {/* Submit */}
        <Button variant="contained" type="submit" disabled={!isValid || !!fileError || !file}>
          Send
        </Button>
      </Stack>

      {/* Preview Dialog */}
      {/* <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="md">
        <DialogContent>
          {previewUrl && (
            <iframe
              src={previewUrl}
              width="100%"
              height="500px"
              style={{ border: "none", borderRadius: "8px" }}
              title="PDF Preview"
            />
          )}
        </DialogContent>
      </Dialog> */}
    </>
  );
}
